import { ref, onUnmounted } from '@vue/composition-api'

/**
 * Composable for wheel audio effects using Web Audio API
 * No external audio files required - generates sounds programmatically
 */
export function useWheelAudio(options = {}) {
  const { enabled = true, volume = 0.5 } = options

  // State
  const isEnabled = ref(enabled)
  const currentVolume = ref(volume)
  let audioContext = null

  /**
   * Initialize or get AudioContext (lazy initialization)
   * Must be called after user interaction due to browser autoplay policies
   */
  function getAudioContext() {
    if (!audioContext) {
      audioContext = new (window.AudioContext || window.webkitAudioContext)()
    }
    // Resume if suspended (browser policy)
    if (audioContext.state === 'suspended') {
      audioContext.resume()
    }
    return audioContext
  }

  /**
   * Play a tick sound (for passing segments)
   */
  function playTick() {
    if (!isEnabled.value) return

    try {
      const ctx = getAudioContext()
      const oscillator = ctx.createOscillator()
      const gainNode = ctx.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(ctx.destination)

      // Short, high-frequency click
      oscillator.type = 'sine'
      oscillator.frequency.value = 800

      // Quick fade out for crisp click
      gainNode.gain.setValueAtTime(currentVolume.value * 0.15, ctx.currentTime)
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.05)

      oscillator.start(ctx.currentTime)
      oscillator.stop(ctx.currentTime + 0.05)
    } catch (e) {
      console.warn('Audio tick failed:', e)
    }
  }

  /**
   * Play spinning sound (continuous during spin)
   * Returns a stop function
   */
  function playSpinning() {
    if (!isEnabled.value) return () => {}

    try {
      const ctx = getAudioContext()
      const oscillator = ctx.createOscillator()
      const gainNode = ctx.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(ctx.destination)

      // Low rumble for spinning
      oscillator.type = 'triangle'
      oscillator.frequency.value = 100

      gainNode.gain.setValueAtTime(currentVolume.value * 0.05, ctx.currentTime)

      oscillator.start()

      // Return stop function
      return () => {
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3)
        oscillator.stop(ctx.currentTime + 0.3)
      }
    } catch (e) {
      console.warn('Spinning sound failed:', e)
      return () => {}
    }
  }

  /**
   * Play win celebration sound (ascending notes)
   */
  function playWin() {
    if (!isEnabled.value) return

    try {
      const ctx = getAudioContext()
      // C5, E5, G5, C6 - ascending major chord
      const frequencies = [523, 659, 784, 1047]

      frequencies.forEach((freq, i) => {
        setTimeout(() => {
          const oscillator = ctx.createOscillator()
          const gainNode = ctx.createGain()

          oscillator.connect(gainNode)
          gainNode.connect(ctx.destination)

          oscillator.type = 'sine'
          oscillator.frequency.value = freq

          // Smooth envelope
          gainNode.gain.setValueAtTime(0, ctx.currentTime)
          gainNode.gain.linearRampToValueAtTime(
            currentVolume.value * 0.2,
            ctx.currentTime + 0.02
          )
          gainNode.gain.exponentialRampToValueAtTime(
            0.01,
            ctx.currentTime + 0.3
          )

          oscillator.start(ctx.currentTime)
          oscillator.stop(ctx.currentTime + 0.3)
        }, i * 100)
      })
    } catch (e) {
      console.warn('Win sound failed:', e)
    }
  }

  /**
   * Play button click sound
   */
  function playClick() {
    if (!isEnabled.value) return

    try {
      const ctx = getAudioContext()
      const oscillator = ctx.createOscillator()
      const gainNode = ctx.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(ctx.destination)

      // Pop sound
      oscillator.type = 'sine'
      oscillator.frequency.setValueAtTime(600, ctx.currentTime)
      oscillator.frequency.exponentialRampToValueAtTime(
        200,
        ctx.currentTime + 0.1
      )

      gainNode.gain.setValueAtTime(currentVolume.value * 0.2, ctx.currentTime)
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1)

      oscillator.start(ctx.currentTime)
      oscillator.stop(ctx.currentTime + 0.1)
    } catch (e) {
      console.warn('Click sound failed:', e)
    }
  }

  /**
   * Play slow down tick (lower pitch as wheel slows)
   * @param {number} progress - 0 to 1, where 1 is near stop
   */
  function playSlowTick(progress) {
    if (!isEnabled.value) return

    try {
      const ctx = getAudioContext()
      const oscillator = ctx.createOscillator()
      const gainNode = ctx.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(ctx.destination)

      // Lower pitch and longer duration as it slows
      oscillator.type = 'sine'
      oscillator.frequency.value = 800 - progress * 400 // 800Hz -> 400Hz

      const duration = 0.05 + progress * 0.1 // 50ms -> 150ms

      gainNode.gain.setValueAtTime(currentVolume.value * 0.15, ctx.currentTime)
      gainNode.gain.exponentialRampToValueAtTime(
        0.01,
        ctx.currentTime + duration
      )

      oscillator.start(ctx.currentTime)
      oscillator.stop(ctx.currentTime + duration)
    } catch (e) {
      console.warn('Slow tick failed:', e)
    }
  }

  /**
   * Set audio enabled state
   */
  function setEnabled(value) {
    isEnabled.value = value
  }

  /**
   * Set volume (0 to 1)
   */
  function setVolume(value) {
    currentVolume.value = Math.max(0, Math.min(1, value))
  }

  /**
   * Cleanup audio context
   */
  function dispose() {
    if (audioContext) {
      audioContext.close()
      audioContext = null
    }
  }

  // Cleanup on unmount
  onUnmounted(() => {
    dispose()
  })

  return {
    // State
    isEnabled,
    currentVolume,

    // Methods
    playTick,
    playSpinning,
    playWin,
    playClick,
    playSlowTick,
    setEnabled,
    setVolume,
    dispose
  }
}

/**
 * Composable for haptic feedback
 */
export function useHaptic(options = {}) {
  const { enabled = true } = options

  const isEnabled = ref(enabled)
  const isSupported = ref('vibrate' in navigator)

  /**
   * Trigger haptic feedback
   * @param {string|number|number[]} pattern - 'light', 'medium', 'heavy', 'win', or custom pattern
   */
  function trigger(pattern = 'light') {
    if (!isEnabled.value || !isSupported.value) return

    const patterns = {
      light: 10,
      medium: 25,
      heavy: 50,
      tick: 5,
      spin: 30,
      win: [50, 30, 100, 30, 150]
    }

    const vibration = typeof pattern === 'string' ? patterns[pattern] : pattern

    try {
      navigator.vibrate(vibration)
    } catch (e) {
      console.warn('Haptic failed:', e)
    }
  }

  /**
   * Set haptic enabled state
   */
  function setEnabled(value) {
    isEnabled.value = value
  }

  return {
    isEnabled,
    isSupported,
    trigger,
    setEnabled
  }
}
